import React, { useCallback, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { useAppDispatch } from 'hooks/reduxHooks'
import { pluralizeToString } from 'lib/string/pluralize'
import { initialiseBnblOfferModal } from 'actions/BnblModalActions'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getBnblPackageViews, getDurationPackageOptions, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import Heading from 'components/Luxkit/Typography/Heading'
import Caption from 'components/Luxkit/Typography/Caption'
import TextButton from 'components/Luxkit/Button/TextButton'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import LineCheckIcon from 'components/Luxkit/Icons/line/LineCheckIcon'
import { BnblSteps, getBnblPackageViewByNumberOfRooms } from 'components/OfferPage/BnblModal/utils'
import FlashHotelRateOptionVariant from 'components/OfferPage/FlashHotel/FlashHotelMobileVariant/FlashHotelRateOptionVariant'
import FlashHotelOptionPriceDetailsBNBLVariant from 'components/OfferPage/FlashHotel/FlashHotelMobileVariant/FlashHotelOptionPriceDetailsBNBLVariant'

export const StyledModalContent = styled(ModalContent)`
  padding: 0;
  background-color: ${props => props.theme.palette.neutral.default.six};
`

export const RoomHeaderContainer = styled(Group)`
  padding: ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

interface Props {
  offer: App.Offer;
  isGift: boolean;
  setStep: (step: BnblSteps) => void;
  packageView: OfferPackageOptionView;
  numberOfRooms: number;
  baseBnblPackageViews: Array<OfferPackageOptionView>;
  selectedPackageUniqueKey: string;
  setSelectedPackageUniqueKey: (uniqueKey: string) => void;
}

function BnblChangePackageStep({
  offer,
  isGift,
  setStep,
  packageView,
  numberOfRooms,
  baseBnblPackageViews,
  selectedPackageUniqueKey,
  setSelectedPackageUniqueKey,
}: Props) {
  const dispatch = useAppDispatch()

  const { duration, roomType } = packageView.package

  const packageViews = useMemo(() => baseBnblPackageViews.filter(pkg => pkg.package.duration === duration && pkg.package.roomType?.id === roomType?.id), [baseBnblPackageViews, duration, roomType])

  const onSelectPackage = useCallback((newPackageUniqueKey: string) => {
    setSelectedPackageUniqueKey(newPackageUniqueKey)
    const newSelectedPackageView = baseBnblPackageViews.find(baseBnblPackageView => baseBnblPackageView.package.uniqueKey === newPackageUniqueKey)
    if (!newSelectedPackageView) return

    const newPackages = [{
      packageView: getBnblPackageViewByNumberOfRooms(newSelectedPackageView, numberOfRooms),
      numberOfRooms,
    }]

    dispatch(initialiseBnblOfferModal(newPackages, offer.id, isGift))
    setStep(isGift ? BnblSteps.GIFT : BnblSteps.HOME)
  }, [baseBnblPackageViews, dispatch, numberOfRooms, offer, setSelectedPackageUniqueKey, setStep, isGift])

  return (
    <StyledModalContent>
      <Group direction="vertical" gap={16}>
        <RoomHeaderContainer direction="vertical" gap={4}>
          <Heading variant="heading5">{roomType?.name}</Heading>
          {!!packageViews?.length && <Caption variant="large">{pluralizeToString('option', packageViews.length)} available</Caption>}
        </RoomHeaderContainer>
        {packageViews?.map((view, index) => <Pane key={view.package.uniqueKey}>
          <FlashHotelRateOptionVariant
            offer={offer}
            optionNumber={index + 1}
            packageView={view}
            showOptionLabel={packageViews.length > 1}
          >
            <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
              <FlashHotelOptionPriceDetailsBNBLVariant packageView={view} />
              {view.package.uniqueKey === selectedPackageUniqueKey && (
                <BodyTextBlock variant="medium" startIcon={<LineCheckIcon />}>
                  Selected
                </BodyTextBlock>
              )}
              {view.package.uniqueKey !== selectedPackageUniqueKey && (
                <TextButton kind="primary" onClick={() => onSelectPackage(view.package.uniqueKey)}>
                  Select package
                </TextButton>
              )}
            </Group>
          </FlashHotelRateOptionVariant>
        </Pane>)}
      </Group>
    </StyledModalContent>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State, ownProps: Props) => {
  const bnblModalPackages = appState.offer.bnblModal[ownProps.offer.id]
  const { numberOfRooms } = bnblModalPackages[0]

  return {
    numberOfRooms,
    packageOptions: getDurationPackageOptions(state, appState),
    baseBnblPackageViews: getBnblPackageViews(state),
  }
})(BnblChangePackageStep)
